/**
 * The run-results readout and the AI triage verdict used to render as two
 * unconnected floating objects on the run-detail page: a wide gauge card,
 * then a bare triage headline with card-in-a-card finding boxes below it.
 * This is the single lockup that replaces both — a compact results column
 * (the same sizing/primitives `LiveRunCard` uses for its rail card) on the
 * left, sharing one border with the triage verdict + findings on the right.
 *
 * Source-agnostic: a native suite run and an external suite run both render
 * through this one component. The caller supplies pre-derived `counts` and
 * `items` — this component doesn't know or care whether they came from a
 * `WorkflowTestExecutionDto`, an external run's `summary.counts`, or (for a
 * multi-area external run with no area selected) one icon per area rather
 * than per test.
 *
 * @module features/triage/RunTriageLockup
 */
import type { ReactNode } from 'react';
import { Card, Flex, Heading, Progress, StatusIconBar, Text, type StatusIconBarItem } from '@chatsdet/ui';
import { calculatePassRate, formatPassRate } from '@chatsdet/types';
import { MATCH_LOCKUP_BORDER } from '../../ui/pages/runs/test-runs/matchLockupBorder';
import { TriageSection, VerdictBadge } from './TriageSection';
import type { TriageSubject } from './useTriage';

export type RunTriageLockupCounts = {
  /** Always counts TESTS — independent of what `completed`/`total` count below. */
  passed: number;
  failed: number;
  skipped: number;
  degraded?: number;
  /**
   * What "N/M complete" and the progress bar draw from. Ordinarily this
   * counts the same tests as `passed`/`failed`/`skipped` above (and a caller
   * omits `progressNoun`) — but when `progressNoun` is set, the caller has
   * chosen to report progress over a DIFFERENT unit than the pass/fail
   * counts (e.g. "N/M areas complete" on a multi-area external run, whose
   * `passed`/`failed` above still count individual tests). Read `completed`/
   * `total` together with whatever `progressNoun` says they're counting.
   */
  completed: number;
  total: number;
};

/**
 * How long after a run finishes its triage row might still land. Observed on
 * prod run `fa35bf09`: the row appeared ~12 minutes after the run finished,
 * and the pipeline's own budget is up to ~20 minutes (a 10-minute diagnostics
 * deadline plus up to a 10-minute agent) — 30 gives that margin.
 */
const AWAITING_TRIAGE_WINDOW_MS = 30 * 60 * 1000;

type RunTriageLockupProps = {
  counts: RunTriageLockupCounts;
  items: StatusIconBarItem[];
  triageSubject: TriageSubject;
  onItemSelect: (item: StatusIconBarItem) => void;
  /**
   * What one square (or, for a multi-area external run, one area) represents,
   * for the empty-state copy. "workflow" for a native suite run; "test" for an
   * external run.
   */
  itemNoun?: string;
  /**
   * What `counts.completed`/`counts.total` are counting, when it isn't the
   * items squares themselves — e.g. "areas" for a multi-area external run's
   * areas-overview scope, where the squares are areas but the pass/fail
   * counts above them are still tests. Every other caller omits this and
   * keeps the plain "N/N complete" reading, since for them completed/total
   * already describes the same items the squares draw.
   */
  progressNoun?: string;
  /** Drives the empty-state copy and whether the fallback verdict may claim "all clear". */
  isRunInProgress: boolean;
  /**
   * When the run/execution finished, or `null` while it hasn't. Combined with
   * `isRunInProgress` to decide whether a still-missing triage row is worth
   * continuing to poll for (see `AWAITING_TRIAGE_WINDOW_MS`) — a triage row is
   * created well after the run itself finishes, so "in progress" alone is not
   * enough to know the pipeline might still produce one.
   */
  finishedAt: string | null;
  /**
   * Rendered full-width after the results/triage grid, inside the same card.
   * Used by the external run detail page to fold its live-progress readout
   * (shard/retry/elapsed line + phase rail) into this card instead of
   * floating it as a separate card above. Omit for callers with nothing to
   * show here.
   */
  footer?: ReactNode;
};

export function RunTriageLockup({
  counts,
  items,
  triageSubject,
  onItemSelect,
  itemNoun = 'workflow',
  progressNoun,
  isRunInProgress,
  finishedAt,
  footer,
}: RunTriageLockupProps) {
  const settled = counts.passed + counts.failed;
  const passRate = calculatePassRate(counts.passed, counts.failed);
  const degraded = counts.degraded ?? 0;
  const awaitingTriage =
    isRunInProgress ||
    (finishedAt != null && Date.now() - new Date(finishedAt).getTime() <= AWAITING_TRIAGE_WINDOW_MS);

  return (
    <Card padding="none" className={`overflow-hidden ${MATCH_LOCKUP_BORDER}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr]">
        <div className="p-4 border-b lg:border-b-0 lg:border-r border-border">
          {items.length > 0 ? (
            <>
              <Heading level={4} size="2xl" className="tabular-nums leading-none">
                {passRate === null ? '—' : formatPassRate(passRate)}
              </Heading>
              <Text size="xs" className="text-muted-foreground">
                pass rate
              </Text>

              <div className="mt-3 space-y-0.5">
                <Text size="sm" color="success">
                  {counts.passed} passed
                </Text>
                {/* Muted at zero — a red "0 failed" reads as an alert that isn't one. */}
                <Text size="sm" color={counts.failed > 0 ? 'destructive' : 'muted'}>
                  {counts.failed} failed
                </Text>
                {counts.skipped > 0 && (
                  <Text size="sm" className="text-muted-foreground">
                    {counts.skipped} skipped
                  </Text>
                )}
                {degraded > 0 && (
                  <Text size="sm" className="text-muted-foreground">
                    {degraded} degraded
                  </Text>
                )}
              </div>

              <Text size="xs" className="text-muted-foreground tabular-nums mt-3">
                {counts.completed}/{counts.total} {progressNoun ? `${progressNoun} ` : ''}complete
              </Text>
              <Progress
                value={counts.total > 0 ? (counts.completed / counts.total) * 100 : 0}
                size="sm"
                className="mt-2"
                aria-label="Run completion"
              />

              <div className="mt-3">
                <StatusIconBar items={items} onItemPress={onItemSelect} />
              </div>
            </>
          ) : counts.total > 0 ? (
            // Counts are known but there's nothing to draw a square for — a
            // native run whose per-workflow rows didn't come back, or an
            // external run whose caller chose not to pass per-test items for
            // this scope (e.g. a huge multi-area run with no area selected).
            <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
              <Text size="sm" weight="medium">
                {counts.completed}/{counts.total} {itemNoun}s completed
              </Text>
              <div className="mt-3 space-y-0.5">
                <Text size="sm" color="success">
                  {counts.passed} passed
                </Text>
                <Text size="sm" color="destructive">
                  {counts.failed} failed
                </Text>
                {counts.skipped > 0 && (
                  <Text size="sm" className="text-muted-foreground">
                    {counts.skipped} skipped
                  </Text>
                )}
                {degraded > 0 && (
                  <Text size="sm" className="text-muted-foreground">
                    {degraded} degraded (not run)
                  </Text>
                )}
              </div>
              <Text size="xs" className="text-muted-foreground mt-3">
                Per-{itemNoun} details are unavailable for this run.
              </Text>
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
              <Text className="text-muted-foreground">
                {isRunInProgress
                  ? `Activity will appear here as ${itemNoun}s complete…`
                  : `No completed ${itemNoun}s yet`}
              </Text>
            </div>
          )}
        </div>

        <div className="p-4 min-w-0">
          <TriageSection
            subject={triageSubject}
            awaitingTriage={awaitingTriage}
            fallback={
              <RunVerdictFallback
                failed={counts.failed}
                settled={settled}
                inProgress={isRunInProgress}
              />
            }
          />
        </div>
      </div>
      {footer}
    </Card>
  );
}

// Exported for unit testing only — not consumed outside this module.
export function RunVerdictFallback({
  failed,
  settled,
  inProgress,
}: {
  failed: number;
  settled: number;
  inProgress: boolean;
}) {
  const tests = settled === 1 ? 'test' : 'tests';

  // A triage row only exists once the suite/run finishes, so this fallback is
  // what renders for an in-flight run too. Gate "all clear" on the run
  // actually being done — a green all-clear badge mid-run reads as "the run
  // finished and triage cleared it", which is false on both counts when other
  // work is still running or queued.
  if (settled > 0 && failed === 0 && !inProgress) {
    return (
      <Flex direction="column" gap="xs">
        <VerdictBadge verdict="all_clear" />
        <Text size="md">
          {settled === 1 ? 'The only test passed' : `All ${settled} tests passed`}
        </Text>
      </Flex>
    );
  }

  if (settled > 0 && failed === 0) {
    // In flight with no failures yet — no badge, since there's no verdict to
    // show: the run isn't done and nothing has failed. State the partial
    // truth without a total (this component only receives `failed`/`settled`).
    return (
      <Text size="sm" className="text-muted-foreground">
        {settled} {tests} passed so far — run still in progress
      </Text>
    );
  }

  if (settled > 0) {
    // Only the run verdict — nothing about triage. `TriageSection` renders this
    // for a fetch error and while the query is still in flight as well as for
    // "no findings", so a line like "No triage findings for this run" would be
    // a claim this component is in no position to make. This claim holds
    // in-flight too: failures already observed stay failures regardless of
    // what the rest of the run does — but mid-run it is a partial count, not
    // a final one, and "so far" says so.
    return (
      <Flex direction="column" gap="xs">
        <VerdictBadge verdict="needs_attention" />
        <Text size="md">
          {failed} of {settled} {tests} failed{inProgress ? ' so far' : ''}
        </Text>
      </Flex>
    );
  }

  // Nothing settled. `settled` counts only passes and failures, so a finished
  // run that was entirely skipped or degraded lands here too — gate the
  // in-progress copy on run state, the same signal the left column's empty
  // branch uses, rather than announcing work that will never arrive.
  return (
    <Text size="sm" className="text-muted-foreground">
      {inProgress ? 'Results will appear here as tests complete…' : 'No tests ran'}
    </Text>
  );
}
